import { useSyncExternalStore } from "react";

/**
 * The visitor's language, chosen once and remembered.
 *
 * The marketing site hands it over as `?lang=fr` alongside `?url=…`; after that
 * the choice lives in localStorage so a refresh, the sign-in round trip, or a
 * return visit keeps it. Only a visitor with no stored choice falls back to the
 * browser's language.
 */
export type Locale = "en" | "fr";

export const LOCALES: readonly Locale[] = ["en", "fr"];

const STORAGE_KEY = "siteagain_locale";
const listeners = new Set<() => void>();
let current: Locale = resolveInitialLocale();
applyDocumentLanguage(current);

function parseLocale(value: string | null | undefined): Locale | null {
  const normalized = value?.trim().toLowerCase().slice(0, 2);
  return normalized === "en" || normalized === "fr" ? normalized : null;
}

function readStored(): Locale | null {
  try { return parseLocale(localStorage.getItem(STORAGE_KEY)); } catch { return null; }
}

function persist(locale: Locale) {
  try { localStorage.setItem(STORAGE_KEY, locale); } catch {}
}

function applyDocumentLanguage(locale: Locale) {
  if (typeof document !== "undefined") document.documentElement.lang = locale;
}

function resolveInitialLocale(): Locale {
  if (typeof window === "undefined") return "en";
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = parseLocale(params.get("lang"));
    if (params.has("lang")) {
      params.delete("lang");
      const query = params.toString();
      window.history.replaceState(window.history.state, "", `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`);
    }
    if (fromUrl) {
      persist(fromUrl);
      return fromUrl;
    }
  } catch {}
  const stored = readStored();
  if (stored) return stored;
  const preferred = (navigator.languages ?? [navigator.language]).map(parseLocale).find((value) => value !== null);
  return preferred ?? "en";
}

export function getLocale(): Locale {
  return current;
}

export function setLocale(next: Locale) {
  persist(next);
  if (next === current) return;
  current = next;
  applyDocumentLanguage(next);
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function useLocale(): Locale {
  return useSyncExternalStore(subscribe, getLocale, getLocale);
}

export function intlLocale(locale: Locale): string {
  return locale === "fr" ? "fr-FR" : "en-GB";
}

export function formatDay(value: string, locale: Locale): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(intlLocale(locale), { day: "numeric", month: "long", year: "numeric" }).format(date);
}
